'use client'

import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Send, CheckCircle2, AlertCircle, Sparkles } from 'lucide-react'

interface ContactModalFormData {
  name: string
  email: string
  service: string
}

const services = [
  'Digital Strategy Audit',
  'Web Development',
  'Videography / Production',
  'Graphic Design / Branding',
  'Technical SEO',
]

export default function ContactModal() {
  const [isOpen, setIsOpen] = useState(false)
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm<ContactModalFormData>()

  useEffect(() => {
    // Check if shown this session
    const hasBeenShown = sessionStorage.getItem('contact-modal-shown')
    if (hasBeenShown) return

    const handleTrigger = () => {
      // Re-verify session storage to avoid race conditions
      if (sessionStorage.getItem('contact-modal-shown')) return
      
      setIsOpen(true)
      sessionStorage.setItem('contact-modal-shown', 'true')
      
      // Cleanup: once triggered, we don't need the other listeners/timers
      document.removeEventListener('mouseleave', handleExitIntent)
      if (timeoutId) clearTimeout(timeoutId)
    }

    const handleExitIntent = (e: MouseEvent) => {
      if (e.clientY <= 0) {
        handleTrigger()
      }
    }

    const timeoutId = setTimeout(() => {
      handleTrigger()
    }, 20000)

    document.addEventListener('mouseleave', handleExitIntent)

    return () => {
      if (timeoutId) clearTimeout(timeoutId)
      document.removeEventListener('mouseleave', handleExitIntent)
    }
  }, [])

  const onSubmit = async (data: ContactModalFormData) => {
    setStatus('loading')
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          brief: `LEAD FROM POPUP: Interested in ${data.service}`,
        }),
      })
      if (res.ok) {
        setStatus('success')
        setTimeout(() => {
          setIsOpen(false)
          setStatus('idle')
          reset()
        }, 3000)
      } else {
        setStatus('error')
      }
    } catch {
      setStatus('error')
    }
  }

  const inputClass = 'w-full bg-[#1E293B]/50 border border-white/10 px-4 py-3 text-sm text-[#F8FAFC] outline-none focus:border-[#C8A96E]/50 transition-all placeholder:text-[#94A3B8]/50'

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop with Flex Centering */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1000] bg-[#020617]/80 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
            onClick={() => setIsOpen(false)}
          >
            {/* Modal Container */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              transition={{ type: 'spring', duration: 0.6, bounce: 0.3 }}
              className="relative w-full max-w-[500px] max-h-[calc(100vh-4rem)] overflow-y-auto bg-[#0F172A] border border-[#C8A96E]/30 p-8 lg:p-12 shadow-[0_0_50px_rgba(200,169,110,0.1)] custom-scrollbar"
              onClick={(e) => e.stopPropagation()} // Stop click bubbling to backdrop
            >
              {/* Abstract Background Decoration */}
              <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#C8A96E]/5 blur-[80px] pointer-events-none" />
              <div className="absolute -left-20 -bottom-20 h-64 w-64 rounded-full bg-[#4FC3F7]/5 blur-[80px] pointer-events-none" />

              {/* Close Button */}
              <button
                onClick={() => setIsOpen(false)}
                className="absolute right-4 top-4 text-[#94A3B8] hover:text-[#C8A96E] transition-colors z-10"
                aria-label="Close contact modal"
              >
                <X size={24} aria-hidden="true" />
              </button>

              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="mb-6 rounded-full bg-[#C8A96E]/10 p-4"
                  >
                    <CheckCircle2 size={48} className="text-[#C8A96E]" />
                  </motion.div>
                  <h3 className="text-3xl text-[#F8FAFC] mb-4" style={{ fontFamily: 'var(--font-bebas, "Bebas Neue", cursive)', letterSpacing: '0.05em' }}>
                    WE'LL BE IN TOUCH
                  </h3>
                  <p className="text-[#94A3B8] text-sm">Our experts are reviewing your request. Prepare for transformation.</p>
                </div>
              ) : (
                <>
                  <div className="mb-8">
                    <div className="flex items-center gap-2 mb-4">
                      <Sparkles size={16} className="text-[#C8A96E]" />
                      <span className="text-[#C8A96E] text-[10px] tracking-[0.3em]" style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}>EXPERT AUDIT</span>
                    </div>
                    <h2 className="text-4xl lg:text-5xl text-[#F8FAFC] leading-none mb-4" style={{ fontFamily: 'var(--font-bebas, "Bebas Neue", cursive)', letterSpacing: '0.02em' }}>
                      GET A FREE <span className="text-[#C8A96E]">STRATEGY</span> CALL
                    </h2>
                    <p className="text-[#94A3B8] text-sm leading-relaxed">
                      Ready to scale? Book a complimentary 15-minute audit to discuss your digital storytelling and tech strategy.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                      <input
                        {...register('name', { required: 'Name is required' })}
                        placeholder="Your Name"
                        className={inputClass}
                      />
                      {errors.name && <span className="text-[10px] text-red-400 mt-1">{errors.name.message}</span>}
                    </div>
                    <div>
                      <input
                        {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Invalid email' } })}
                        placeholder="email@example.com"
                        className={inputClass}
                      />
                      {errors.email && <span className="text-[10px] text-red-400 mt-1">{errors.email.message}</span>}
                    </div>
                    <div>
                      <label htmlFor="modal-service" className="sr-only">Specialized service</label>
                      <select
                        id="modal-service"
                        {...register('service', { required: 'Select a service' })}
                        className={inputClass}
                        style={{ background: '#1E293B', appearance: 'none' }}
                      >
                        <option value="" disabled>Select specialized service</option>
                        {services.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      {errors.service && <span className="text-[10px] text-red-400 mt-1">{errors.service.message}</span>}
                    </div>

                    {status === 'error' && (
                      <div className="flex items-center gap-2 text-red-400 text-[10px] bg-red-400/10 p-3">
                        <AlertCircle size={14} /> Failed to submit. Please try again.
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className="w-full flex items-center justify-center gap-3 bg-[#C8A96E] py-4 text-[#0F172A] text-xs font-bold tracking-[0.2em] hover:bg-[#D4BA8A] transition-all disabled:opacity-70 group"
                      style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
                    >
                      {status === 'loading' ? 'PROCESSING...' : (
                        <>REQUEST CALLBACK <Send size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" /></>
                      )}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
